import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { inr, num, dateFmt } from "@/lib/format";
import { PageLoader, EmptyState, StatusBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Printer, Minus, Plus, Loader2, PackageOpen, Truck, CreditCard } from "lucide-react";

const PAY_STYLES = {
  paid: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
  pending: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
  unpaid: "bg-muted text-muted-foreground",
  failed: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400",
  verification_failed: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400",
};
const PAY_LABELS = { paid: "Paid", pending: "Pending", unpaid: "Unpaid", failed: "Failed", verification_failed: "Failed" };

function postToPayu({ action, fields }) {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = action;
  Object.entries(fields).forEach(([k, v]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = k;
    input.value = String(v ?? "");
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

export default function Reprint() {
  const qc = useQueryClient();
  const { data: books } = useQuery({ queryKey: ["author-books"], queryFn: async () => (await api.get("/author/books")).data });
  const { data: orders, isLoading } = useQuery({ queryKey: ["author-orders"], queryFn: async () => (await api.get("/author/reprint-orders")).data });

  const [bookId, setBookId] = useState("");
  const [qty, setQty] = useState(10);
  const [address, setAddress] = useState("");
  const [mobile, setMobile] = useState("");
  const [pincode, setPincode] = useState("");
  const [note, setNote] = useState("");
  const [detail, setDetail] = useState(null);

  const book = books?.find((b) => b.id === bookId);
  const total = book ? book.printing_rate * qty : 0;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pay = params.get("payment");
    if (!pay) return;
    const order = params.get("order");
    if (pay === "success") {
      toast.success(`Payment successful${order ? ` for ${order}` : ""}. Your reprint order is confirmed.`);
    } else {
      toast.error(`Payment${order ? ` for ${order}` : ""} was not completed. You can retry from your order history.`);
    }
    qc.invalidateQueries({ queryKey: ["author-orders"] });
    window.history.replaceState({}, "", window.location.pathname);
  }, [qc]);

  const submit = useMutation({
    mutationFn: async () => {
      const order = (await api.post("/author/reprint-orders", { book_id: bookId, quantity: qty, shipping_address: address, mobile, pincode, note })).data;
      return (await api.post(`/author/reprint-orders/${order.id}/pay`)).data;
    },
    onSuccess: (pay) => {
      setBookId(""); setQty(10); setAddress(""); setMobile(""); setPincode(""); setNote("");
      postToPayu(pay);
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const payNow = useMutation({
    mutationFn: async (oid) => (await api.post(`/author/reprint-orders/${oid}/pay`)).data,
    onSuccess: (pay) => postToPayu(pay),
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const valid = bookId && qty >= 10 && address.trim() && /^\d{10}$/.test(mobile.trim()) && /^\d{6}$/.test(pincode.trim());

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Order your books</p>
        <p className="text-sm text-muted-foreground mt-1">Purchase printed copies of your books at printing rates (minimum 10 copies).</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 lg:p-8 h-fit">
          <h3 className="font-head text-lg font-medium">New reprint order</h3>
          <div className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label>Book</Label>
              <Select value={bookId} onValueChange={setBookId}>
                <SelectTrigger data-testid="reprint-book-select"><SelectValue placeholder="Select a book" /></SelectTrigger>
                <SelectContent>
                  {books?.map((b) => <SelectItem key={b.id} value={b.id}>{b.title}</SelectItem>)}
                </SelectContent>
              </Select>
              {book && <p className="text-xs text-muted-foreground">Printing rate: <span className="font-medium text-foreground">{inr(book.printing_rate)}</span> / copy</p>}
            </div>

            <div className="space-y-2">
              <Label>Quantity (min 10)</Label>
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" size="icon" className="shrink-0" data-testid="qty-minus" onClick={() => setQty((q) => Math.max(10, q - 5))}>
                  <Minus className="h-4 w-4" />
                </Button>
                <Input data-testid="reprint-qty" type="number" min="10" value={qty} onChange={(e) => setQty(Math.max(10, parseInt(e.target.value) || 10))} className="text-center" />
                <Button type="button" variant="outline" size="icon" className="shrink-0" data-testid="qty-plus" onClick={() => setQty((q) => q + 5)}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Shipping address</Label>
              <Textarea data-testid="reprint-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Full delivery address" className="min-h-[70px]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Mobile no.</Label>
                <Input data-testid="reprint-mobile" inputMode="numeric" maxLength={10} value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))} placeholder="10-digit number" />
              </div>
              <div className="space-y-2">
                <Label>Pincode</Label>
                <Input data-testid="reprint-pincode" inputMode="numeric" maxLength={6} value={pincode} onChange={(e) => setPincode(e.target.value.replace(/\D/g, ""))} placeholder="6-digit pincode" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Note (optional)</Label>
              <Input data-testid="reprint-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Any special instructions" />
            </div>

            {book && (
              <div className="flex items-center justify-between rounded-lg bg-secondary px-4 py-3">
                <span className="text-sm text-secondary-foreground">{num(qty)} copies × {inr(book.printing_rate)}</span>
                <span className="font-head font-bold tabular-nums" data-testid="reprint-total">{inr(total)}</span>
              </div>
            )}

            <Button data-testid="submit-reprint" onClick={() => submit.mutate()} disabled={!valid || submit.isPending} className="w-full gap-2">
              {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Pay &amp; place order
            </Button>
            <p className="text-xs text-muted-foreground text-center">You'll be redirected to PayU to complete payment securely.</p>
          </div>
        </Card>

        <Card className="lg:col-span-2 overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="font-head font-medium">Order history</h3>
          </div>
          {isLoading ? (
            <PageLoader />
          ) : orders.length === 0 ? (
            <EmptyState icon={PackageOpen} title="No orders yet" desc="Your reprint orders will appear here." />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableHead>Order</TableHead>
                    <TableHead>Book</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Tracking</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((o) => (
                    <TableRow key={o.id} data-testid={`my-order-${o.id}`}>
                      <TableCell className="font-mono text-xs">
                        <button data-testid={`view-order-${o.id}`} onClick={() => setDetail(o)} className="text-primary hover:underline">{o.order_no || "—"}</button>
                      </TableCell>
                      <TableCell className="font-medium">{o.book_title}</TableCell>
                      <TableCell className="text-right tabular-nums">{num(o.quantity)}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{inr(o.total_amount)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span data-testid={`payment-badge-${o.id}`} className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${PAY_STYLES[o.payment_status] || PAY_STYLES.unpaid}`}>
                            {PAY_LABELS[o.payment_status] || "Unpaid"}
                          </span>
                          {o.payment_status !== "paid" && (
                            <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" data-testid={`pay-now-${o.id}`} onClick={() => payNow.mutate(o.id)} disabled={payNow.isPending}>
                              {payNow.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <CreditCard className="h-3 w-3" />} Pay
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{dateFmt(o.created_at)}</TableCell>
                      <TableCell><StatusBadge status={o.status} /></TableCell>
                      <TableCell>
                        {o.tracking_link ? (
                          <a href={o.tracking_link} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 inline-flex items-center gap-1 text-sm" data-testid={`track-link-${o.id}`}>
                            <Truck className="h-3.5 w-3.5" /> Track
                          </a>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="order-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Order {detail?.order_no}</DialogTitle>
            <DialogDescription>Placed on {detail ? dateFmt(detail.created_at) : ""}</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 py-1 text-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Book</p>
                  <p className="font-medium" data-testid="detail-book">{detail.book_title}</p>
                </div>
                <div className="flex gap-2">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${PAY_STYLES[detail.payment_status] || PAY_STYLES.unpaid}`}>{PAY_LABELS[detail.payment_status] || "Unpaid"}</span>
                  <StatusBadge status={detail.status} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg bg-muted/50 p-3">
                  <p className="text-xs text-muted-foreground">Quantity</p>
                  <p className="font-head font-bold tabular-nums" data-testid="detail-qty">{num(detail.quantity)}</p>
                </div>
                <div className="rounded-lg bg-muted/50 p-3">
                  <p className="text-xs text-muted-foreground">Rate / copy</p>
                  <p className="font-head font-bold tabular-nums">{inr(detail.rate_per_copy)}</p>
                </div>
                <div className="rounded-lg bg-secondary p-3">
                  <p className="text-xs text-muted-foreground">Amount paid</p>
                  <p className="font-head font-bold tabular-nums text-primary" data-testid="detail-total">{inr(detail.total_amount)}</p>
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Delivery address</p>
                <p className="mt-1 whitespace-pre-wrap" data-testid="detail-address">{detail.shipping_address}</p>
                <p className="mt-1 text-muted-foreground">Mobile: <span className="text-foreground" data-testid="detail-mobile">{detail.mobile || "—"}</span> · Pincode: <span className="text-foreground">{detail.pincode || "—"}</span></p>
              </div>
              {detail.note && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Note</p>
                  <p className="mt-1">{detail.note}</p>
                </div>
              )}
              {detail.payu_payment_id && (
                <p className="text-xs text-muted-foreground">Transaction ID: <span className="font-mono text-foreground">{detail.payu_payment_id}</span></p>
              )}
              {detail.tracking_link && (
                <a href={detail.tracking_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-primary underline underline-offset-2" data-testid="detail-track">
                  <Truck className="h-4 w-4" /> Track your parcel
                </a>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
